/* import __COLOCATED_TEMPLATE__ from './proxy-details.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type ProxyDetail from 'embercom/models/standalone/proxy-detail';

interface Signature {
  Args: {};
}

export default class StandaloneZendeskDeployCommonProxyDetails extends Component<Signature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyError: (message: string) => void;
  };

  @tracked proxyDetail: ProxyDetail | null = null;
  @tracked proxyUrl = '';
  @tracked isSavingProxy = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadProxyDetail();
  }

  private async loadProxyDetail() {
    try {
      let proxyDetails = await this.store.findAll('standalone/proxy-detail');
      let existing = proxyDetails.firstObject ?? null;
      if (existing) {
        this.proxyDetail = existing;
        this.proxyUrl = existing.proxyUrl ?? '';
      }
    } catch {
      // Proxy detail may not exist yet, which is fine
    }
  }

  get isSaveProxyDisabled() {
    return this.isSavingProxy || !this.proxyUrl.trim();
  }

  @action updateProxyUrl(event: Event) {
    let input = event.target as HTMLInputElement;
    this.proxyUrl = input.value;
  }

  @action async saveProxyUrl() {
    this.isSavingProxy = true;
    try {
      if (this.proxyDetail) {
        this.proxyDetail.proxyUrl = this.proxyUrl;
        await this.proxyDetail.save();
      } else {
        let proxyDetail = this.store.createRecord('standalone/proxy-detail', {
          proxyUrl: this.proxyUrl,
        });
        try {
          await proxyDetail.save();
          this.proxyDetail = proxyDetail;
        } catch (e: unknown) {
          proxyDetail.unloadRecord();
          throw e;
        }
      }
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.zendesk.deploy.proxy.save-success'),
      );
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.deploy.proxy.save-error'),
      );
    } finally {
      this.isSavingProxy = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::Deploy::Common::ProxyDetails': typeof StandaloneZendeskDeployCommonProxyDetails;
    'standalone/zendesk/deploy/common/proxy-details': typeof StandaloneZendeskDeployCommonProxyDetails;
  }
}
